/** Shared geometry for the EU Cities bubble chart — used by the SVG component, the GIF/PNG composite and the
 *  MP4 renderer so every output puts the same city at the same place. */
export const log2 = (v) => Math.log(v) / Math.LN2;
export const pow2 = (e) => Math.pow(2, e);

/** Domain over a set of rows (one frame, or all frames for a stable timeline grid). */
export function chartDomain(rows) {
  const nprs = rows.map((r) => r.nights_per_resident), shares = rows.map((r) => r.foreign_share_pct);
  return {
    lo: log2(Math.min(...nprs) / 1.25), hi: log2(Math.max(...nprs) * 1.25),   // 25 % pad, not snapped → room for the 8–32 cluster
    yLo: Math.max(0, Math.floor((Math.min(...shares) - 6) / 10) * 10), yHi: 100,
    maxN: Math.max(...rows.map((r) => r.nights_total)),
  };
}

/** Scales for a plot box {l,t,iw,ih}: x log2, y linear, bubble radius ∝ sqrt(nights). */
export function chartScales(box, dom, rMaxDivisor = 19) {
  const { l, t, iw, ih } = box;
  const x = (v) => l + ((log2(v) - dom.lo) / (dom.hi - dom.lo)) * iw;
  const y = (v) => t + (1 - (v - dom.yLo) / (dom.yHi - dom.yLo)) * ih;
  const rMax = Math.max(20, iw / rMaxDivisor);
  const rad = (v) => Math.max(4, Math.sqrt(v / dom.maxN) * rMax);
  const xt = []; for (let e = Math.ceil(dom.lo); e <= Math.floor(dom.hi); e++) xt.push(pow2(e));
  const yt = []; for (let v = dom.yLo; v <= dom.yHi; v += 10) yt.push(v);
  return { x, y, rad, xt, yt };
}

export const toPoints = (rows, box, scales) => rows.map((r) => ({
  ...r, cx: scales.x(r.nights_per_resident), cy: scales.y(r.foreign_share_pct), r: scales.rad(r.nights_total),
  minX: box.l, maxX: box.l + box.iw, minY: box.t, maxY: box.t + box.ih,
}));

/** Greedy label placement: right → left → top → bottom. Pass 1 avoids other labels AND other bubbles (shrunk 25 %);
 *  pass 2 avoids labels and big bubbles; only big bubbles fall back to an overlap. `measure(name)` → label width px. */
export function placeLabels(pts, measure = (name) => name.length * 6.4, h = 12) {
  const boxes = [];
  const out = {};
  const hit = (b, list) => list.some((o) => !(b.x2 < o.x1 || b.x1 > o.x2 || b.y2 < o.y1 || b.y1 > o.y2));
  const bubbleBox = (p) => ({ x1: p.cx - p.r * 0.75, x2: p.cx + p.r * 0.75, y1: p.cy - p.r * 0.75, y2: p.cy + p.r * 0.75, city: p.city });
  const bubbles = pts.map(bubbleBox);
  const gap = h / 3;
  [...pts].sort((a, b) => b.r - a.r).forEach((p) => {
    const w = measure(p.name);
    const cands = [
      { dx: p.r + gap, dy: h / 3, anchor: "start", bx: p.cx + p.r + gap },
      { dx: -p.r - gap, dy: h / 3, anchor: "end", bx: p.cx - p.r - gap - w },
      { dx: 0, dy: -p.r - gap - 1, anchor: "middle", bx: p.cx - w / 2 },
      { dx: 0, dy: p.r + h, anchor: "middle", bx: p.cx - w / 2 },
    ].map((c) => ({ ...c, box: { x1: c.bx, x2: c.bx + w, y1: p.cy + c.dy - h + 2, y2: p.cy + c.dy + 2 } }))
      .filter((c) => c.box.x1 >= p.minX && c.box.x2 <= p.maxX && c.box.y1 >= p.minY && c.box.y2 <= p.maxY);
    const others = bubbles.filter((b) => b.city !== p.city);
    const big = others.filter((b) => b.x2 - b.x1 >= 2 * h);
    const pick = cands.find((c) => !hit(c.box, boxes) && !hit(c.box, others))
      || cands.find((c) => !hit(c.box, boxes) && !hit(c.box, big))
      || (p.r >= 22 ? cands.find((c) => !hit(c.box, boxes)) || cands[0] : null);
    if (pick) { boxes.push(pick.box); out[p.city] = pick; }
  });
  return out;
}
